import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root'
})
export class LocalesService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient, private storage: StorageService) { }

  getLocales(): Observable<any[]> {
    const params = this.appendAuthParam();
    return this.http.get<any[]>(`${this.apiUrl}/locales`, { params });
  }
  
  /**
   * Obtiene una lista paginada y filtrada de locales.
   * @param page Número de página.
   * @param limit Cantidad de ítems por página.
   * @param clientId ID del cliente para filtrar.
   * @param search Término de búsqueda.
   * @param sortBy Campo por el cual ordenar.
   * @param sortOrder Dirección del ordenamiento ('ASC' o 'DESC').
   * @returns Observable con los datos y el total de locales.
   */
  getLocalesPaginados(
    page: number = 1, 
    limit: number = 10, 
    clientId?: number, 
    search?: string,
    sortBy?: string,
    sortOrder?: string
  ): Observable<{data: any[], total: number}> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());
    
    if (clientId) params = params.set('clientId', clientId.toString());
    if (search) params = params.set('search', search);
    if (sortBy) params = params.set('sortBy', sortBy);
    if (sortOrder) params = params.set('sortOrder', sortOrder);
    
    // Inyectamos el userId
    params = this.appendAuthParam(params);
    
    return this.http.get<{data: any[], total: number}>(`${this.apiUrl}/locales`, { params });
  }

  /**
   * Obtiene todos los locales que coinciden con los filtros para exportar a Excel.
   * @param clientId ID del cliente para filtrar.
   * @param search Término de búsqueda.
   * @param sortBy Campo por el cual ordenar.
   * @param sortOrder Dirección del ordenamiento ('ASC' o 'DESC').
   * @returns Observable con los datos para exportar.
   */
  getLocalesParaExportar(
    clientId?: number,
    search?: string,
    sortBy?: string,
    sortOrder?: string
  ): Observable<any> {
    let params = new HttpParams().set('export', 'true'); 

    if (clientId) params = params.set('clientId', clientId.toString());
    if (search) params = params.set('search', search);
    if (sortBy) params = params.set('sortBy', sortBy);
    if (sortOrder) params = params.set('sortOrder', sortOrder);

    // Inyectamos el userId
    params = this.appendAuthParam(params);

    return this.http.get<any>(`${this.apiUrl}/locales`, { params });
  }

  getLocalByNombre(nombre: string): Observable<any> {
    const params = this.appendAuthParam();
    return this.http.get(`${this.apiUrl}/locales/nombre/${nombre}`, { params });
  }

  crearLocal(local: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/locales`, local);
  }

  getLocalById(id: number): Observable<any> {
    const params = this.appendAuthParam();
    return this.http.get(`${this.apiUrl}/locales/${id}`, { params });
  }

  updateLocal(id: number, local: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/locales/${id}`, local);
  }

  deleteLocal(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/locales/${id}`);
  }

  getLocalesByClientId(id: number): Observable<any> {
    // REFACTORIZADO: Pasamos de string en la URL a usar el helper con HttpParams
    const params = this.appendAuthParam();
    return this.http.get(`${this.apiUrl}/locales/cliente/${id}`, { params });
  }

  addSectorToLocal(localId: number, sectorData: Partial<any>): Observable<any> {
    return this.http.post(`${this.apiUrl}/locales/${localId}/sectores`, sectorData);
  }

  // HELPER: Centraliza la inyección del userId en los HttpParams
  private appendAuthParam(params: HttpParams = new HttpParams()): HttpParams {
    const currentUser = this.storage.getCurrentUser();
    if (currentUser?.id) {
      return params.set('userId', currentUser.id.toString());
    }
    return params;
  }
}